/**
 * DateRunsPanel - Left sidebar for date tabs and delivery runs list
 */
import React, { useRef, useState } from 'react';
import { offlineRunPlan } from '@/utils/offlineRunPlan';
import { runSendHold, RUN_SEND_HOLD_MESSAGE } from '@/utils/runSendRecovery';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Plus, Trash2, Route, History, Lock, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import { formatDateForTab, isSameDay } from '@/utils/dateUtils';
import { isBefore, startOfDay } from 'date-fns';
import EmptyState from '@/components/EmptyState';

const DateRunsPanel = ({
  // Date state
  dateTabs,
  selectedDate,
  isPastDate,
  onSelectFutureDate,
  onSelectPastDate,
  getOrderCountForDate,
  getTabLabel,
  filteredOrdersCount,
  // Runs state
  runs,
  selectedRunId,
  onSelectRun,
  onDeleteRun,
  onCreateRunClick,
  runCommands,
  commandStatus,
  // Read-only when the desktop app is offline
  offline = false,
  // Panel minimize state
  isMinimized = false,
  onToggleMinimize,
  // Driver mode - hides admin controls
  driverMode = false
}) => {
  const [armedDeleteRunId, setArmedDeleteRunId] = useState(null);
  const deletePlan = useRef(null);

  const handleDeleteClick = (event, runId) => {
    event.stopPropagation();
    if (runSendHold(runs.find(run => run.id === runId)) || runCommands?.blockedRun(runId)) return;
    if (armedDeleteRunId !== runId) {
      deletePlan.current = { plan: offline ? offlineRunPlan(runs.find(run => run.id === runId)) : null,
        execute: onDeleteRun, offline };
      setArmedDeleteRunId(runId);
      setTimeout(
        () => setArmedDeleteRunId(current => (current === runId ? null : current)),
        3000,
      );
      return;
    }
    setArmedDeleteRunId(null);
    const intent = deletePlan.current;
    if (intent?.offline) intent.execute(runId, intent.plan);
    else intent?.execute(runId);
  };

  return (
    <div 
      className={`absolute top-3 left-3 lg:bottom-3 w-56 lg:w-64 bg-white/95 backdrop-blur-xl rounded-2xl shadow-soft-lg border border-stone-200/50 flex flex-col overflow-hidden z-10 transition-all duration-200 ${
        isMinimized ? 'max-h-[52px]' : 'max-h-[40vh] lg:max-h-none'
      }`}
      data-testid="left-panel"
    >
      {/* Order count badge + minimize button */}
      <div className={`px-4 py-2.5 border-b border-stone-200/50 ${
        isPastDate ? 'bg-amber-50/80' : 'bg-primary/5'
      }`}>
        <div className="flex items-center justify-between">
          <div className={`min-w-0 flex-1 truncate text-sm font-medium ${isPastDate ? 'text-amber-800' : 'text-stone-800'}`}>
            {isPastDate && <History className="h-3.5 w-3.5 inline mr-1.5" />}
            {filteredOrdersCount} order{filteredOrdersCount !== 1 ? 's' : ''} for {formatDateForTab(selectedDate)}
            {isPastDate && ' (Past)'}
          </div>
          {commandStatus}
          <button
            onClick={onToggleMinimize}
            className="p-1 hover:bg-stone-200/50 rounded-xl transition-colors"
            data-testid="minimize-left-panel"
            title={isMinimized ? 'Expand panel' : 'Minimize panel'}
            aria-label={isMinimized ? 'Expand delivery runs panel' : 'Minimize delivery runs panel'}
          >
            {isMinimized ? (
              <ChevronDown className="h-4 w-4 text-stone-600" />
            ) : (
              <ChevronUp className="h-4 w-4 text-stone-600" />
            )}
          </button>
        </div>
      </div>

      {/* Collapsible content */}
      {!isMinimized && (
        <>
          {/* Date tabs - horizontal scrollable */}
          <div className="flex gap-1 px-3 py-2 border-b border-stone-200/50 overflow-x-auto" data-testid="date-tabs">
            {dateTabs.map((date, index) => {
              const isSelected = selectedDate && isSameDay(date, selectedDate) && !isPastDate;
              const count = getOrderCountForDate(date);
              
              return (
                <button
                  key={date.toISOString()}
                  onClick={() => onSelectFutureDate(date)}
                  className={`flex-shrink-0 px-2.5 py-1.5 text-xs rounded-xl transition-all ${
                    isSelected
                      ? 'bg-primary text-white shadow-soft-md'
                      : 'bg-stone-100/80 text-stone-700 hover:bg-stone-200/80'
                  }`}
                  data-testid={`tab-day-${index}`}
                >
                  <div className="font-medium">{getTabLabel(date, index)}</div>
                  <div className="opacity-80 text-[10px]">({count})</div>
                </button>
              );
            })}
            
            {/* Past Runs Button */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className={`flex-shrink-0 px-2.5 py-1.5 text-xs rounded-xl transition-all ${
                    isPastDate
                      ? 'bg-amber-500 text-white shadow-soft-md'
                      : 'bg-stone-100/80 text-stone-700 hover:bg-stone-200/80'
                  }`}
                  data-testid="past-runs-button"
                >
                  <History className="h-3 w-3 mx-auto mb-0.5" />
                  <div className="font-medium text-[10px]">Past</div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" side="bottom">
                <Calendar
                  mode="single"
                  selected={isPastDate ? selectedDate : undefined}
                  onSelect={onSelectPastDate}
                  disabled={(date) => !isBefore(date, startOfDay(new Date()))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Delivery Runs Section */}
          <div className="flex-1 flex flex-col min-h-0 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                {isPastDate ? (
                  <History className="h-4 w-4 text-amber-600" />
                ) : (
                  <Route className="h-4 w-4 text-primary" />
                )}
                <span className="font-medium text-sm">
                  {driverMode ? 'My Runs' : isPastDate ? 'Past Runs' : 'Delivery Runs'}
                </span>
                <span className="text-xs text-stone-500">({runs.length})</span>
              </div>
              {!driverMode && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onCreateRunClick}
                  disabled={runCommands?.blockedCreate}
                  title={offline ? 'Create a local offline draft run' : undefined}
                  className="h-7 text-xs px-2 bg-white/80"
                  data-testid="create-run-button"
                >
                  <Plus className="h-3 w-3 mr-1" /> New
                </Button>
              )}
            </div>
            
            {/* Runs list - scrollable */}
            <div className="flex-1 overflow-y-auto space-y-1.5">
              {runs.length === 0 ? (
                <EmptyState>
                  {driverMode 
                    ? 'No runs assigned to you for this date.'
                    : 'No runs for this date.\nCreate one to start.'}
                </EmptyState>
              ) : (
                runs.map((run) => {
                  const isOfflineDraft = run.origin === 'local_desktop' || run.is_offline_draft;
                  const canDeleteRun = !driverMode && (!offline || isOfflineDraft);
                  return (
                  <div
                    key={run.id}
                    onClick={() => onSelectRun(run.id)}
                    className={`flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-all text-xs ${
                      selectedRunId === run.id
                        ? 'bg-primary/10 border-primary/30 border shadow-soft-sm'
                        : 'bg-stone-50/80 hover:bg-stone-100/80 border border-transparent'
                    }`}
                    data-testid={`run-item-${run.id}`}
                  >
                    <div className="flex items-center gap-2">
                      {run.status !== 'draft' && (
                        <Lock className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      )}
                      <div>
                        <div className="font-medium flex items-center gap-1.5">
                          {run.name}
                          {run.status === 'committed' && (
                            <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-normal">
                              Committed
                            </span>
                          )}
                          {run.status === 'completed' && (
                            <span className="text-[10px] bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded-full font-normal">
                              Completed
                            </span>
                          )}
                          {isOfflineDraft && (
                            <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-normal">
                              Offline
                            </span>
                          )}
                        </div>
                        <div className="text-stone-500 truncate" title={runSendHold(run) ? RUN_SEND_HOLD_MESSAGE : undefined}>
                          {runSendHold(run) ? 'Send held — check Review' : `${run.stops?.length || 0} stops`}
                        </div>
                      </div>
                    </div>
                    {canDeleteRun && (
                      <Button
                        size="sm"
                        variant={armedDeleteRunId === run.id ? 'destructive' : 'ghost'}
                        className={
                          armedDeleteRunId === run.id
                            ? 'h-6 px-1.5 gap-0.5'
                            : 'h-6 w-6 p-0 hover:bg-red-100'
                        }
                        onClick={(event) => handleDeleteClick(event, run.id)}
                        disabled={!!runSendHold(run) || runCommands?.blockedRun(run.id)}
                        title={armedDeleteRunId === run.id ? 'Click again to confirm deletion' : 'Delete run'}
                        aria-label={armedDeleteRunId === run.id ? 'Confirm run deletion' : 'Delete run'}
                        data-testid={`delete-run-${run.id}`}
                      >
                        {armedDeleteRunId === run.id ? (
                          <>
                            <AlertTriangle className="h-3 w-3" />
                            <span className="text-[9px] leading-none">Sure?</span>
                          </>
                        ) : (
                          <Trash2 className="h-3 w-3 text-red-500" />
                        )}
                      </Button>
                    )}
                  </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRunsPanel;
